import React, { useEffect, useMemo, useRef, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  getDoc,
  limit,
} from "firebase/firestore";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import "./TradeOfferDetail.css";
import TradeWorkflowModal from "../components/TradeWorkflowModal";
import {
  calcSellerSuccessFeeBreakdown,
  formatZar,
  getCashOfferAmount,
} from "../utils/pricing";

const useQuery = () => {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
};

const toMillisSafe = (ts) => {
  try {
    if (!ts) return 0;
    if (typeof ts.toMillis === "function") return ts.toMillis();
    if (typeof ts.toDate === "function") return ts.toDate().getTime();
    if (ts instanceof Date) return ts.getTime();
    if (typeof ts === "number") return ts;
    return 0;
  } catch {
    return 0;
  }
};

const normStr = (v) => String(v || "").trim();
const normLower = (v) => normStr(v).toLowerCase();

const getTradeIdFromOffer = (o) =>
  normStr(o?.tradeId) ||
  normStr(o?.requestedItemId) ||
  normStr(o?.tradeItemId) ||
  "";

const formatRelativeTime = (ts) => {
  const ms = toMillisSafe(ts);
  if (!ms) return "";
  const diff = Math.floor((Date.now() - ms) / 1000);
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(ms).toLocaleDateString();
};

const isWorkflowStep = (type) => {
  const t = normLower(type);
  return (
    t.startsWith("risk_") ||
    t.includes("risk") ||
    t.includes("prep") ||
    t.includes("buyer_approval") ||
    t.includes("approval") ||
    t.includes("payment_required") ||
    t.includes("awaiting_payment") ||
    t.includes("payment") ||
    t.includes("inspection") ||
    t.includes("pickup") ||
    t.includes("hub") ||
    t.includes("delivery") ||
    t.includes("schedule") ||
    t.includes("verify") ||
    t.includes("completed") ||
    t.includes("cancel")
  );
};

const normalizeOffer = (o) => {
  const offerType = normLower(o.offerType || o.type || o.offerKind || "");
  const rawStatus = o.status ?? o.offerStatus ?? o.state ?? "pending";
  const status = normLower(rawStatus);

  const rawAmt =
    o.proposedCashAmount ?? o.cashAmount ?? o.amount ?? o.cash ?? null;
  const proposedCashAmount =
    rawAmt == null || rawAmt === ""
      ? null
      : typeof rawAmt === "number"
      ? rawAmt
      : Number(String(rawAmt).replace(/[^\d.-]/g, ""));

  return {
    ...o,
    _ui: {
      offerType: offerType || "—",
      status: status || "pending",
      proposedCashAmount: Number.isFinite(proposedCashAmount)
        ? proposedCashAmount
        : null,
      fromLabel: "Buyer",
      createdAtMs: toMillisSafe(o.createdAt || o.timestamp || o.updatedAt),
    },
  };
};

const normalizeNotif = (n) => {
  const createdAtMs = toMillisSafe(n.createdAt || n.timestamp || n.updatedAt);
  const type = normLower(
    n.type || n.eventType || n.category || n.kind || "system"
  );
  return { ...n, _ui: { createdAtMs, type } };
};

const prettyStage = (s) => {
  const v = normLower(s);
  if (!v) return "—";
  return v.replace(/_/g, " ");
};

const stageAlias = (rawStage) => {
  const s = normLower(rawStage);

  const CANON = new Set([
    "seller_prep",
    "buyer_approval",
    "awaiting_payment",
    "ready_for_inspection",
    "awaiting_dropoff",
    "pickup_scheduled",
    "inspection_passed",
    "pickup_in_progress",
    "hub_inspection_pass",
    "delivery_scheduled",
    "delivery_in_progress",
    "delivered_pending_buyer_verification",
    "verification_passed",
    "completed",
    "cancelled",
  ]);

  if (CANON.has(s)) return s;

  if (
    ["locked_pending_risk_review", "risk_review", "pending_risk_review"].includes(
      s
    )
  )
    return "seller_prep";
  if (
    ["awaiting_buyer_approval", "buyer_review", "buyer_confirm"].includes(s)
  )
    return "buyer_approval";
  if (
    [
      "payment_required",
      "pending_payment",
      "cash_pending",
      "awaiting_cash_payment",
    ].includes(s)
  )
    return "awaiting_payment";
  if (
    ["ready_for_pickup_inspection", "pickup_ready", "awaiting_inspection"].includes(
      s
    )
  )
    return "ready_for_inspection";
  if (["pickup_booked"].includes(s)) return "pickup_scheduled";
  if (["pickup_inspection_pass"].includes(s)) return "inspection_passed";
  if (["hub_inspection_passed"].includes(s)) return "hub_inspection_pass";
  if (["delivery_booked"].includes(s)) return "delivery_scheduled";
  if (["delivered"].includes(s)) return "delivered_pending_buyer_verification";
  if (["buyer_verified", "verification_ok"].includes(s))
    return "verification_passed";

  return s || "";
};

function OfferInboxPanel({
  offers,
  loadingOffers,
  selectedOfferId,
  tradeId,
  navigate,
  setSelectedOfferId,
  didAutoSelectRef,
  isAdmin,
  tradeOwnerUid,
  statusPillClass,
}) {
  return (
    <div className="tod-card">
      <div className="tod-card-header">
        <h3 className="tod-card-title">All offers</h3>
        {loadingOffers ? (
          <span className="tod-pill">Loading…</span>
        ) : (
          <span className="tod-pill">{offers.length}</span>
        )}
      </div>

      <div className="tod-card-body">
        {loadingOffers ? (
          <div className="tod-empty">Loading offers…</div>
        ) : !offers.length ? (
          <div className="tod-empty">
            No offers yet.
            {isAdmin && tradeOwnerUid ? (
              <div className="tod-empty" style={{ padding: 0, marginTop: 10 }}>
                Support note: offers are scoped to the sellerUid for this trade.
                <div style={{ marginTop: 6 }}>
                  sellerUid scope: <code>{tradeOwnerUid}</code>
                </div>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="tod-offer-list">
            {offers.map((o) => {
              const status = o._ui.status;
              const isSelected = o.id === selectedOfferId;
              const when = o.createdAt || o.timestamp || o.updatedAt;

              return (
                <div
                  key={o.id}
                  id={`offer-${o.id}`}
                  className={`tod-offer-item ${isSelected ? "is-selected" : ""}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    didAutoSelectRef.current = true;
                    setSelectedOfferId(o.id);
                    navigate(
                      `/trade/${encodeURIComponent(tradeId)}/offers?offerId=${encodeURIComponent(o.id)}`,
                      { replace: true }
                    );
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      didAutoSelectRef.current = true;
                      setSelectedOfferId(o.id);
                      navigate(
                        `/trade/${encodeURIComponent(tradeId)}/offers?offerId=${encodeURIComponent(o.id)}`,
                        { replace: true }
                      );
                    }
                  }}
                >
                  <div className="tod-offer-row-top">
                    <div className="tod-offer-main">
                      <p className="tod-offer-heading">
                        <span style={{ textTransform: "capitalize" }}>
                          {o._ui.offerType}
                        </span>
                        {o._ui.offerType === "cash" &&
                          Number.isFinite(o._ui.proposedCashAmount) && (
                            <span
                              className="tod-pill tod-pill--primary"
                              style={{ padding: "4px 8px" }}
                            >
                              {o._ui.proposedCashAmount.toLocaleString("en-ZA", {
                                style: "currency",
                                currency: "ZAR",
                              })}
                            </span>
                          )}
                        {isSelected && (
                          <span
                            className="tod-pill tod-pill--primary"
                            style={{ padding: "4px 8px" }}
                          >
                            Selected
                          </span>
                        )}
                      </p>

                      <p className="tod-offer-meta">
                        <span className="tod-from-label">From</span>{" "}
                        <span className="tod-from-value">
                          {o._ui.fromLabel || "Buyer"}
                        </span>
                        {when ? (
                          <>
                            <span className="tod-muted-inline"> • </span>
                            <span>{formatRelativeTime(when)}</span>
                          </>
                        ) : null}
                      </p>
                    </div>

                    <div className="tod-offer-side">
                      <span
                        className={statusPillClass(status)}
                        style={{ padding: "6px 10px" }}
                      >
                        {status}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function SellerPayoutPreview({ offer }) {
  if (!offer) return null;

  const offerType = normLower(offer?._ui?.offerType || offer?.offerType || offer?.type);
  const cashAmount = getCashOfferAmount(offer);

  if (offerType !== "cash" || cashAmount <= 0) return null;

  const fee = calcSellerSuccessFeeBreakdown(cashAmount);

  return (
    <div className="offer-fee-preview">
      <div className="offer-fee-preview__header">
        <span>Seller payout estimate</span>
        <strong>Cash offer</strong>
      </div>

      <div className="offer-fee-preview__row">
        <span>Buyer offer</span>
        <strong>{formatZar(fee.itemAmountZar)}</strong>
      </div>

      <div className="offer-fee-preview__row">
        <span>NexterZone success fee</span>
        <strong>-{formatZar(fee.successFeeZar)}</strong>
      </div>

      <div className="offer-fee-preview__total">
        <span>Estimated seller payout</span>
        <strong>{formatZar(fee.sellerNetAmountZar)}</strong>
      </div>

      <p>
        The buyer pays only the accepted offer amount. The NexterZone success fee
        is deducted from your seller payout after the trade is completed.
      </p>
    </div>
  );
}

function OfferDecisionPanel({
  selectedOffer,
  copied,
  copyToClipboard,
  decide,
  statusPillClass,
  actionBusy,
}) {
  return (
    <div className="tod-card">
      <div className="tod-card-header">
        <h3 className="tod-card-title">Offer details</h3>
        {selectedOffer?._ui?.status ? (
          <span className={statusPillClass(selectedOffer._ui.status)}>
            {selectedOffer._ui.status}
          </span>
        ) : (
          <span className="tod-pill">—</span>
        )}
      </div>

      <div className="tod-card-body">
        {!selectedOffer ? (
          <div className="tod-empty">
            Select an offer to view details and take action.
          </div>
        ) : (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div className="tod-k" style={{ marginBottom: 6 }}>
                  Offer ID
                </div>
                <div
                  className="tod-mono"
                  style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
                >
                  <span>{selectedOffer.id}</span>
                </div>
              </div>

              <button
                type="button"
                className="tod-btn"
                onClick={() => copyToClipboard(selectedOffer.id)}
                title="Copy offer ID"
              >
                {copied ? "Copied" : "Copy"}
              </button>
            </div>

            <div className="tod-divider" />

            <div className="tod-kv">
              <div className="tod-k">Type</div>
              <div className="tod-v" style={{ textTransform: "capitalize" }}>
                {selectedOffer._ui.offerType}
              </div>

              {selectedOffer._ui.offerType === "cash" && (
                <>
                  <div className="tod-k">Amount</div>
                  <div className="tod-v">
                    {(selectedOffer._ui.proposedCashAmount || 0).toLocaleString(
                      "en-ZA",
                      {
                        style: "currency",
                        currency: "ZAR",
                      }
                    )}
                  </div>
                </>
              )}

              <div className="tod-k">From</div>
              <div className="tod-v">{selectedOffer._ui.fromLabel || "Buyer"}</div>

              <div className="tod-k">Status</div>
              <div className="tod-v">
                <span className={statusPillClass(selectedOffer._ui.status)}>
                  {selectedOffer._ui.status}
                </span>
              </div>
            </div>
            <SellerPayoutPreview offer={selectedOffer} />

            {selectedOffer._ui.status === "pending" ? (
              <div className="tod-actions">
                <button
                  type="button"
                  className="tod-btn tod-btn--success"
                  onClick={() => decide(selectedOffer.id, "accepted")}
                  disabled={actionBusy}
                >
                  {actionBusy ? "Processing…" : "Accept"}
                </button>

                <button
                  type="button"
                  className="tod-btn tod-btn--danger"
                  onClick={() => decide(selectedOffer.id, "declined")}
                  disabled={actionBusy}
                >
                  {actionBusy ? "Processing…" : "Decline"}
                </button>
              </div>
            ) : (
              <div className="tod-empty" style={{ padding: 0, marginTop: 12 }}>
                This offer is <strong>{selectedOffer._ui.status}</strong>. Actions
                are disabled.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function AdminWorkflowPanel({
  selectedOffer,
  loadingPrivate,
  privateOffer,
  loadingWorkflow,
  wfStageCanon,
  wfStageRaw,
  workflow,
  trade,
  paymentConfirmed,
  loadingLogistics,
  logistics,
  wfTradeType,
  isCashTrade,
  grossAmount,
  platformFee,
  netToSeller,
  adminNote,
  setAdminNote,
  pickupStart,
  setPickupStart,
  pickupEnd,
  setPickupEnd,
  deliveryStart,
  setDeliveryStart,
  deliveryEnd,
  setDeliveryEnd,
  canSchedulePickup,
  canInspect,
  canStartPickup,
  canScheduleDelivery,
  canComplete,
  onSchedulePickup,
  onRecordInspection,
  onStartPickup,
  onScheduleDelivery,
  onCompleteTrade,
  routeLabel,
}) {
  if (!selectedOffer) return null;

  return (
    <>
      <div className="tod-card tod-admin-card" style={{ marginTop: 14 }}>
        <div className="tod-card-header">
          <h4 className="tod-card-title tod-admin-title">
            Admin details <span className="tod-admin-badge">@nexterzone.com</span>
          </h4>
        </div>

        <div className="tod-card-body">
          {loadingPrivate ? (
            <div className="tod-empty">Loading private fields…</div>
          ) : !privateOffer ? (
            <div className="tod-empty">No private record found for this offer.</div>
          ) : (
            <div className="tod-admin-grid">
              <div className="tod-admin-k">Buyer email</div>
              <div className="tod-admin-v">{privateOffer.buyerEmail || "—"}</div>

              <div className="tod-admin-k">Seller email</div>
              <div className="tod-admin-v">{privateOffer.sellerEmail || "—"}</div>

              <div className="tod-admin-k">Buyer UID</div>
              <div className="tod-admin-v">{privateOffer.buyerUid || "—"}</div>

              <div className="tod-admin-k">Seller UID</div>
              <div className="tod-admin-v">{privateOffer.sellerUid || "—"}</div>
            </div>
          )}
        </div>
      </div>

      <div className="tod-card tod-admin-card" style={{ marginTop: 14 }}>
        <div className="tod-card-header">
          <h4 className="tod-card-title tod-admin-title">
            Workflow controls{" "}
            <span className="tod-admin-badge">{routeLabel}</span>
          </h4>
        </div>

        <div className="tod-card-body">
          <div className="tod-admin-grid" style={{ marginBottom: 10 }}>
            <div className="tod-admin-k">Workflow stage</div>
            <div className="tod-admin-v">
              {loadingWorkflow ? "Loading…" : prettyStage(wfStageCanon || wfStageRaw)}
            </div>

            <div className="tod-admin-k">Accepted offer</div>
            <div className="tod-admin-v">
              {workflow?.acceptedOfferId || trade?.acceptedOfferId || "—"}
            </div>

            <div className="tod-admin-k">Payment gate</div>
            <div className="tod-admin-v">
              <span className="tod-pill" style={{ padding: "4px 8px" }}>
                {paymentConfirmed ? "confirmed" : "pending"}
              </span>
              <span style={{ marginLeft: 8 }}>
                {paymentConfirmed
                  ? "✅ Financial gate cleared"
                  : "⚠️ Block gated actions until payment is confirmed"}
              </span>
            </div>

            <div className="tod-admin-k">Pickup</div>
            <div className="tod-admin-v">
              {loadingLogistics
                ? "Loading…"
                : logistics?.pickup?.status
                ? `${logistics.pickup.status} (${logistics.pickup.windowStart || "?"} → ${
                    logistics.pickup.windowEnd || "?"
                  })`
                : "—"}
            </div>

            <div className="tod-admin-k">Delivery</div>
            <div className="tod-admin-v">
              {loadingLogistics
                ? "Loading…"
                : logistics?.delivery?.status
                ? `${logistics.delivery.status} (${logistics.delivery.windowStart || "?"} → ${
                    logistics.delivery.windowEnd || "?"
                  })`
                : "—"}
            </div>

            <div className="tod-admin-k">Trade type</div>
            <div className="tod-admin-v">{wfTradeType || "—"}</div>

            {isCashTrade && (
              <>
                <div className="tod-admin-k">Gross amount</div>
                <div className="tod-admin-v">
                  {grossAmount != null
                    ? Number(grossAmount).toLocaleString("en-ZA", {
                        style: "currency",
                        currency: "ZAR",
                      })
                    : "—"}
                </div>

                <div className="tod-admin-k">Platform fee</div>
                <div className="tod-admin-v">
                  {platformFee != null
                    ? Number(platformFee).toLocaleString("en-ZA", {
                        style: "currency",
                        currency: "ZAR",
                      })
                    : "—"}
                </div>

                <div className="tod-admin-k">Net to seller</div>
                <div className="tod-admin-v">
                  {netToSeller != null
                    ? Number(netToSeller).toLocaleString("en-ZA", {
                        style: "currency",
                        currency: "ZAR",
                      })
                    : "—"}
                </div>
              </>
            )}
          </div>

          <div className="tod-divider" />

          <div className="tod-k" style={{ marginTop: 10 }}>
            Admin note (optional)
          </div>
          <input
            className="tod-input"
            value={adminNote}
            onChange={(e) => setAdminNote(e.target.value)}
            placeholder="Internal note"
            style={{ width: "100%", marginTop: 6 }}
          />

          <div className="tod-divider" />

          <div className="tod-k">Schedule pickup window</div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
              marginTop: 6,
            }}
          >
            <input
              className="tod-input"
              value={pickupStart}
              onChange={(e) => setPickupStart(e.target.value)}
              placeholder='windowStart (e.g., "2026-01-04 10:00")'
            />
            <input
              className="tod-input"
              value={pickupEnd}
              onChange={(e) => setPickupEnd(e.target.value)}
              placeholder='windowEnd (e.g., "2026-01-04 14:00")'
            />
          </div>

          <div className="tod-actions" style={{ marginTop: 10 }}>
            <button
              type="button"
              className="tod-btn tod-btn--primary"
              onClick={onSchedulePickup}
              disabled={!canSchedulePickup}
            >
              Schedule Pickup
            </button>
          </div>

          <div className="tod-divider" />

          <div className="tod-k">Inspection (final acceptance)</div>
          <div className="tod-actions" style={{ marginTop: 10 }}>
            <button
              type="button"
              className="tod-btn tod-btn--success"
              onClick={() => onRecordInspection(true)}
              disabled={!canInspect}
            >
              Inspection: PASS
            </button>

            <button
              type="button"
              className="tod-btn tod-btn--danger"
              onClick={() => onRecordInspection(false)}
              disabled={!canInspect}
            >
              Inspection: FAIL
            </button>
          </div>

          <div className="tod-divider" />

          <div className="tod-k">Pickup start (releases payout)</div>
          <div className="tod-actions" style={{ marginTop: 10 }}>
            <button
              type="button"
              className="tod-btn tod-btn--success"
              onClick={onStartPickup}
              disabled={!canStartPickup}
            >
              Start Pickup (Release Payout)
            </button>
          </div>

          <div className="tod-divider" />

          <div className="tod-k">Schedule delivery window</div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
              marginTop: 6,
            }}
          >
            <input
              className="tod-input"
              value={deliveryStart}
              onChange={(e) => setDeliveryStart(e.target.value)}
              placeholder='windowStart (e.g., "2026-01-06 10:00")'
            />
            <input
              className="tod-input"
              value={deliveryEnd}
              onChange={(e) => setDeliveryEnd(e.target.value)}
              placeholder='windowEnd (e.g., "2026-01-06 14:00")'
            />
          </div>

          <div className="tod-actions" style={{ marginTop: 10 }}>
            <button
              type="button"
              className="tod-btn tod-btn--primary"
              onClick={onScheduleDelivery}
              disabled={!canScheduleDelivery}
            >
              Schedule Delivery
            </button>
          </div>

          <div className="tod-divider" />

          <div className="tod-actions" style={{ marginTop: 10 }}>
            <button
              type="button"
              className="tod-btn tod-btn--success"
              onClick={onCompleteTrade}
              disabled={!canComplete}
            >
              Complete Trade
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function ProcessTimelinePanel({ loadingTimeline, timeline }) {
  return (
    <div className="tod-card" style={{ marginTop: 14 }}>
      <div className="tod-card-header">
        <h3 className="tod-card-title">Process timeline</h3>
        <span className="tod-pill">{loadingTimeline ? "Loading…" : timeline.length}</span>
      </div>

      <div className="tod-card-body">
        {loadingTimeline ? (
          <div className="tod-empty">Loading process…</div>
        ) : timeline.length === 0 ? (
          <div className="tod-empty">No process updates yet for this trade.</div>
        ) : (
          <div className="tod-timeline">
            {timeline.map((n) => {
              const when = n.createdAt || n.timestamp || n.updatedAt;
              return (
                <div key={n.id} className="tod-event">
                  <div className="tod-event-dot" />
                  <div className="tod-event-row">
                    <h4 className="tod-event-title">
                      {n.message || n.title || "Update"}
                    </h4>
                    <span className="tod-event-time">
                      {formatRelativeTime(when)}
                    </span>
                  </div>

                  <div className="tod-event-body">
                    {n?._ui?.type ? (
                      <span
                        className="tod-pill"
                        style={{ padding: "4px 8px", marginRight: 8 }}
                      >
                        {n._ui.type}
                      </span>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const TradeOfferDetail = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const qparams = useQuery();
  const { tradeId: routeTradeId } = useParams();

  const tradeId = normStr(routeTradeId);
  const focusOfferId = qparams.get("offerId") || "";

  const [trade, setTrade] = useState(null);
  const [offers, setOffers] = useState([]);
  const [loadingOffers, setLoadingOffers] = useState(true);

  const [timeline, setTimeline] = useState([]);
  const [loadingTimeline, setLoadingTimeline] = useState(true);

  const [selectedOfferId, setSelectedOfferId] = useState(focusOfferId);
  const clickLockRef = useRef(false);
  const didAutoSelectRef = useRef(false);

  const isAdmin =
    !!currentUser?.email &&
    !!currentUser?.emailVerified &&
    normLower(currentUser.email).endsWith("@nexterzone.com");

  const VIEW_MODE = isAdmin ? "admin" : "seller_offer";
  const WORKFLOW_MODAL_MODE = isAdmin ? "admin" : "full_workflow";

  const [privateOffer, setPrivateOffer] = useState(null);
  const [loadingPrivate, setLoadingPrivate] = useState(false);

  const [workflow, setWorkflow] = useState(null);
  const [loadingWorkflow] = useState(true);

  const [logistics, setLogistics] = useState(null);
  const [loadingLogistics, setLoadingLogistics] = useState(false);

  const [pickupStart, setPickupStart] = useState("");
  const [pickupEnd, setPickupEnd] = useState("");
  const [deliveryStart, setDeliveryStart] = useState("");
  const [deliveryEnd, setDeliveryEnd] = useState("");
  const [adminNote, setAdminNote] = useState("");
  const [copied, setCopied] = useState(false);
  const [wfOpen, setWfOpen] = useState(false);

  const [canView, setCanView] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionBanner, setActionBanner] = useState({ type: "", text: "" });

  const sessionUid = normStr(currentUser?.uid);
  const sessionLoading = !sessionUid;

  useEffect(() => {
    if (!actionBanner.text) return;
    const timer = setTimeout(() => {
      setActionBanner({ type: "", text: "" });
    }, 4500);
    return () => clearTimeout(timer);
  }, [actionBanner]);

  useEffect(() => {
    if (focusOfferId) {
      didAutoSelectRef.current = true;
      setSelectedOfferId(focusOfferId);
    }
  }, [focusOfferId]);

useEffect(() => {
  if (!tradeId || !currentUser?.uid) return;

  let cancelled = false;

  const run = async () => {
    try {
      const uid = normStr(currentUser.uid);
      const email = normLower(currentUser.email);

      let t = null;

      // 1. Load the trade first.
      const tRef = doc(db, "Trades", tradeId);
      const tSnap = await getDoc(tRef);

      if (tSnap.exists()) {
        t = { id: tSnap.id, ...tSnap.data() };
      }

      // 2. Check ownership from the trade document.
      const tradeOwnerUid =
        normStr(t?.ownerId) ||
        normStr(t?.ownerUid) ||
        normStr(t?.sellerUid);

      const isOwnerByUid = !!tradeOwnerUid && tradeOwnerUid === uid;

      const isOwnerByEmailLegacy =
        !!t?.ownerEmail && !!email && normLower(t.ownerEmail) === email;

      let allowed = !!(isAdmin || isOwnerByUid || isOwnerByEmailLegacy);

      // 3. If trade ownership check fails, fall back to the offer record.
      // This is important because sellerUid is reliably stored on TradeOffers.
      if (!allowed && focusOfferId) {
        const offerRef = doc(db, "TradeOffers", focusOfferId);
        const offerSnap = await getDoc(offerRef);

        if (offerSnap.exists()) {
          const offer = { id: offerSnap.id, ...offerSnap.data() };

          const offerTradeId = getTradeIdFromOffer(offer);
          const offerSellerUid = normStr(offer?.sellerUid);
          const offerSellerEmail = normLower(offer?.sellerEmail);

          const offerBelongsToThisTrade = offerTradeId === tradeId;
          const userIsOfferSeller =
            (!!offerSellerUid && offerSellerUid === uid) ||
            (!!offerSellerEmail && offerSellerEmail === email);

          allowed = !!(
            isAdmin ||
            (offerBelongsToThisTrade && userIsOfferSeller)
          );
        }
      }

      if (cancelled) return;

      if (!allowed) {
        console.warn("[TradeOfferDetail] Access denied. Redirecting to trade page.", {
          tradeId,
          focusOfferId,
          uid,
          email,
          tradeOwnerUid,
        });

        navigate(`/trade/${tradeId}`, { replace: true });
        return;
      }

      setTrade(t || { id: tradeId });
      setCanView(true);
    } catch (e) {
      console.error("[TradeOfferDetail] Failed to load trade/offer access:", e);

      if (!cancelled) {
        setTrade(null);
        setCanView(false);
      }
    }
  };

  run();

  return () => {
    cancelled = true;
  };
}, [
  tradeId,
  focusOfferId,
  currentUser?.uid,
  currentUser?.email,
  navigate,
  isAdmin,
]);

  const tradeOwnerUid = useMemo(() => {
    const t = trade || {};
    return normStr(t?.ownerId) || normStr(t?.ownerUid) || normStr(t?.sellerUid) || "";
  }, [trade]);

  const sellerScopeUid = useMemo(() => {
    return isAdmin ? tradeOwnerUid : normStr(currentUser?.uid);
  }, [isAdmin, tradeOwnerUid, currentUser?.uid]);

  useEffect(() => {
    if (!tradeId || !canView) return;
    if (isAdmin && !tradeOwnerUid) return;
    if (!sellerScopeUid) return;

    let cancelled = false;
    setLoadingOffers(true);
    setOffers([]);

    const offersCol = collection(db, "TradeOffers");

    const makeQueries = (withSellerUid) => {
      const filters = [];
      if (withSellerUid) filters.push(where("sellerUid", "==", sellerScopeUid));

      return [
        query(
          offersCol,
          where("tradeId", "==", tradeId),
          ...filters,
          orderBy("createdAt", "desc"),
          limit(200)
        ),
        query(
          offersCol,
          where("requestedItemId", "==", tradeId),
          ...filters,
          orderBy("createdAt", "desc"),
          limit(200)
        ),
        query(
          offersCol,
          where("tradeItemId", "==", tradeId),
          ...filters,
          orderBy("createdAt", "desc"),
          limit(200)
        ),
      ];
    };

    const [qA, qB, qC] = makeQueries(true);
    const [qA2, qB2, qC2] = isAdmin ? makeQueries(false) : [null, null, null];

    let latestA = [];
    let latestB = [];
    let latestC = [];
    let latestA2 = [];
    let latestB2 = [];
    let latestC2 = [];

    const mergeAndApply = (useFallback = false) => {
      const m = new Map();
      const add = (arr) => arr.forEach((r) => m.set(r.id, r));

      add(latestA);
      add(latestB);
      add(latestC);

      if (useFallback && isAdmin) {
        add(latestA2);
        add(latestB2);
        add(latestC2);
      }

      const rows = Array.from(m.values())
        .filter((o) => getTradeIdFromOffer(o) === tradeId)
        .map(normalizeOffer);

      rows.sort((a, b) => (b._ui.createdAtMs || 0) - (a._ui.createdAtMs || 0));

      if (cancelled) return;

      setOffers(rows);
      setLoadingOffers(false);

      setSelectedOfferId((prev) => {
        if (prev) return prev;
        if (didAutoSelectRef.current) return prev;
        const preferred = rows[0]?.id || "";
        if (preferred) {
          didAutoSelectRef.current = true;
          navigate(
            `/trade/${encodeURIComponent(tradeId)}/offers?offerId=${encodeURIComponent(preferred)}`,
            { replace: true }
          );
        return preferred;
        }
        return "";
      });
    };

    let unsubA2 = null;
    let unsubB2 = null;
    let unsubC2 = null;
    let fallbackAttached = false;

    const attachFallbackIfNeeded = () => {
      if (!isAdmin || fallbackAttached || !qA2 || !qB2 || !qC2) return;
      const primaryCount =
        (latestA?.length || 0) + (latestB?.length || 0) + (latestC?.length || 0);
      if (primaryCount > 0) return;

      fallbackAttached = true;

      unsubA2 = onSnapshot(qA2, (ss) => {
        latestA2 = ss.docs.map((d) => ({ id: d.id, ...d.data() }));
        mergeAndApply(true);
      });

      unsubB2 = onSnapshot(qB2, (ss) => {
        latestB2 = ss.docs.map((d) => ({ id: d.id, ...d.data() }));
        mergeAndApply(true);
      });

      unsubC2 = onSnapshot(qC2, (ss) => {
        latestC2 = ss.docs.map((d) => ({ id: d.id, ...d.data() }));
        mergeAndApply(true);
      });
    };

    const unsubA = onSnapshot(qA, (ss) => {
      latestA = ss.docs.map((d) => ({ id: d.id, ...d.data() }));
      mergeAndApply(false);
      attachFallbackIfNeeded();
    });

    const unsubB = onSnapshot(qB, (ss) => {
      latestB = ss.docs.map((d) => ({ id: d.id, ...d.data() }));
      mergeAndApply(false);
      attachFallbackIfNeeded();
    });

    const unsubC = onSnapshot(qC, (ss) => {
      latestC = ss.docs.map((d) => ({ id: d.id, ...d.data() }));
      mergeAndApply(false);
      attachFallbackIfNeeded();
    });

    return () => {
      cancelled = true;
      unsubA?.();
      unsubB?.();
      unsubC?.();
      unsubA2?.();
      unsubB2?.();
      unsubC2?.();
    };
  }, [tradeId, canView, sellerScopeUid, navigate, isAdmin, tradeOwnerUid]);

  useEffect(() => {
    if (!tradeId || VIEW_MODE !== "admin") return;

    setLoadingTimeline(true);
    setTimeline([]);

    const notifsCol = collection(db, "Notifications");
    const qTL = query(
      notifsCol,
      where("tradeId", "==", tradeId),
      orderBy("createdAt", "asc"),
      limit(300)
    );

    const unsub = onSnapshot(
      qTL,
      (ss) => {
        const rows = ss.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .map(normalizeNotif)
          .filter((n) => isWorkflowStep(n?._ui?.type) || n?._ui?.type?.includes("offer"));

        rows.sort((a, b) => (a._ui.createdAtMs || 0) - (b._ui.createdAtMs || 0));
        setTimeline(rows);
        setLoadingTimeline(false);
      },
      (err) => {
        console.warn("[TradeOfferDetail] timeline snapshot error:", err);
        setTimeline([]);
        setLoadingTimeline(false);
      }
    );

    return () => unsub();
  }, [tradeId, VIEW_MODE]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (VIEW_MODE !== "admin" || !selectedOfferId) {
        if (!cancelled) {
          setPrivateOffer(null);
          setLoadingPrivate(false);
        }
        return;
      }

      try {
        setLoadingPrivate(true);
        const ref = doc(db, "TradeOffersPrivate", selectedOfferId);
        const snap = await getDoc(ref);
        if (cancelled) return;
        setPrivateOffer(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      } catch {
        if (!cancelled) setPrivateOffer(null);
      } finally {
        if (!cancelled) setLoadingPrivate(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [VIEW_MODE, selectedOfferId]);

  useEffect(() => {
    if (!tradeId || VIEW_MODE !== "admin") return;

    const ref = doc(db, "TradeWorkflows", tradeId);

    const unsub = onSnapshot(
      ref,
      (snap) => {
        setWorkflow(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      },
      () => {
        setWorkflow(null);
      }
    );

    return () => unsub();
  }, [tradeId, VIEW_MODE]);

  useEffect(() => {
    setLogistics(null);
    setLoadingLogistics(false);

    if (!tradeId || VIEW_MODE !== "admin") return;

    setLoadingLogistics(true);
    const ref = doc(db, "TradeLogistics", tradeId);

    const unsub = onSnapshot(
      ref,
      (snap) => {
        setLogistics(snap.exists() ? { id: snap.id, ...snap.data() } : null);
        setLoadingLogistics(false);
      },
      () => {
        setLogistics(null);
        setLoadingLogistics(false);
      }
    );

    return () => unsub();
  }, [tradeId, VIEW_MODE]);

  const decide = async (offerId, decision) => {
    if (!tradeId || !offerId) return;
    if (clickLockRef.current || actionBusy) return;

    clickLockRef.current = true;
    setActionBusy(true);
    setActionBanner({ type: "", text: "" });

    try {
      const functions = getFunctions(undefined, "us-central1");
      const call =
        decision === "accepted"
          ? httpsCallable(functions, "acceptOffer")
          : httpsCallable(functions, "declineOffer");

      await call({ tradeId, offerId });

      navigate(
        `/trade/${encodeURIComponent(tradeId)}/offers?offerId=${encodeURIComponent(offerId)}`,
        { replace: true }
      );

      if (decision === "accepted") {
        setActionBanner({
          type: "success",
          text: "Offer accepted successfully. Opening the workflow so you can continue with seller prep evidence.",
        });

        setTimeout(() => {
          setWfOpen(true);
        }, 500);
      } else {
        setActionBanner({
          type: "success",
          text: "Offer declined successfully.",
        });
      }
    } catch (e) {
      console.error("[TradeOfferDetail] Decision failed:", e);
      setActionBanner({
        type: "error",
        text: e?.message || "We could not complete that action. Please try again.",
      });
    } finally {
      setActionBusy(false);
      setTimeout(() => {
        clickLockRef.current = false;
      }, 300);
    }
  };

  const adminCall = async (name, payload) => {
    const functions = getFunctions(undefined, "us-central1");
    const fn = httpsCallable(functions, name);
    return await fn(payload);
  };

  const adminCallTry = async (names, payload, friendlyLabel) => {
    const list = Array.isArray(names) ? names : [names];
    let lastErr = null;

    for (const n of list) {
      try {
        return await adminCall(n, payload);
      } catch (e) {
        lastErr = e;
        const msg = String(e?.message || "").toLowerCase();
        const notFound =
          msg.includes("not found") ||
          msg.includes("function") ||
          msg.includes("404") ||
          msg.includes("unknown") ||
          msg.includes("does not exist");
        const precond =
          msg.includes("failed-precondition") ||
          msg.includes("precondition") ||
          msg.includes("stage") ||
          msg.includes("invalid");
        if (precond || !notFound) break;
      }
    }

    alert(lastErr?.message || `${friendlyLabel || "Action"} failed.`);
    throw lastErr;
  };

  const onSchedulePickup = async () => {
    if (!pickupStart || !pickupEnd) {
      alert("Pickup windowStart and windowEnd are required.");
      return;
    }
    await adminCallTry(
      ["schedulePickup"],
      {
        tradeId,
        windowStart: pickupStart,
        windowEnd: pickupEnd,
        note: adminNote || "",
      },
      "Schedule pickup"
    );
  };

  const onRecordInspection = async (pass) => {
    const inspectionType = isPartnerDropoff
      ? "partner_dropoff_intake"
      : "pickup_inspection";

    const inspectionMode = isPartnerDropoff
      ? "partner_dropoff"
      : "platform_pickup";

    await adminCallTry(
      ["recordInspectionResult", "submitPickupInspection"],
      {
        tradeId,
        pass: !!pass,
        serialMatch: true,
        conditionMatch: true,
        note: adminNote || "",
        inspectionType,
        inspectionMode,
      },
      pass ? "Inspection PASS" : "Inspection FAIL"
    );
  };

  const onStartPickup = async () => {
    await adminCallTry(
      ["startPickup", "pickupStart", "markPickupInProgress"],
      {
        tradeId,
        note: adminNote || "",
      },
      "Start pickup"
    );
  };

  const onScheduleDelivery = async () => {
    if (!deliveryStart || !deliveryEnd) {
      alert("Delivery windowStart and windowEnd are required.");
      return;
    }
    await adminCallTry(
      ["scheduleDelivery"],
      {
        tradeId,
        windowStart: deliveryStart,
        windowEnd: deliveryEnd,
        note: adminNote || "",
      },
      "Schedule delivery"
    );
  };

  const onCompleteTrade = async () => {
    await adminCallTry(
      ["completeTrade"],
      {
        tradeId,
        note: adminNote || "",
      },
      "Complete trade"
    );
  };

  const copyToClipboard = async (text) => {
    try {
      if (!text) return;
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {}
  };

  const selectedOffer = useMemo(
    () => offers.find((o) => o.id === selectedOfferId) || null,
    [offers, selectedOfferId]
  );

  const totalOffers = offers.length;
  const cashOffers = offers.filter(
    (o) => o._ui.offerType === "cash" && Number.isFinite(o._ui.proposedCashAmount)
  );
  const highestCash =
    cashOffers.length > 0
      ? cashOffers.reduce(
          (max, o) =>
            o._ui.proposedCashAmount > max ? o._ui.proposedCashAmount : max,
          cashOffers[0]._ui.proposedCashAmount
        )
      : null;

  const latestTs =
    totalOffers > 0 ? offers[0].createdAt || offers[0].timestamp || null : null;
  const tradeStatus = normLower(trade?.status || "");
  const wfStageRaw = normLower(workflow?.stage || "");
  const wfStageCanon = stageAlias(wfStageRaw);
  const wfTradeType = normLower(workflow?.tradeType || trade?.tradeType || "");
  const isCashTrade = wfTradeType === "cash";
  const cashLegacy = normLower(workflow?.cash?.status || "");
  const payStatus = normLower(workflow?.payment?.status || "");

  const paymentConfirmed =
    (!isCashTrade &&
      (payStatus
        ? ["paid", "secured", "confirmed", "settled"].includes(payStatus)
        : true)) ||
    (isCashTrade &&
      (["paid", "secured", "confirmed", "settled"].includes(payStatus) ||
        ["paid", "settled", "secured", "confirmed"].includes(cashLegacy)));

  const platformFee =
    workflow?.cash?.platformFee ??
    workflow?.platformFeeAmount ??
    workflow?.fees?.platformFeeAmount ??
    null;

  const netToSeller = workflow?.cash?.amountNetToSeller ?? null;
  const grossAmount = workflow?.cash?.amountGross ?? null;

  const fulfillmentMethod = normLower(
    workflow?.fulfillmentChoice?.method || workflow?.dropoff?.method || ""
  );

  const isPartnerDropoff =
    fulfillmentMethod === "partner_dropoff" ||
    !!normStr(workflow?.dropoff?.partnerId);

  const isPlatformPickup = !isPartnerDropoff;

  const inspectionFeeStatus = normLower(workflow?.inspectionFee?.status || "");
  const inspectionFeePaid = inspectionFeeStatus === "paid";

  const routeLabel = isPartnerDropoff ? "partner drop-off" : "platform pickup";

  const canSchedulePickup =
    isPlatformPickup &&
    wfStageCanon === "ready_for_inspection" &&
    paymentConfirmed &&
    (!isCashTrade || inspectionFeePaid);

  const canInspect =
    (isPartnerDropoff &&
      wfStageCanon === "awaiting_dropoff" &&
      paymentConfirmed) ||
    (isPlatformPickup &&
      wfStageCanon === "pickup_scheduled" &&
      paymentConfirmed &&
      (!isCashTrade || inspectionFeePaid));

  const canStartPickup = isPlatformPickup && wfStageCanon === "inspection_passed";

  const canScheduleDelivery =
    (isPartnerDropoff && wfStageCanon === "inspection_passed") ||
    ["pickup_in_progress", "hub_inspection_pass"].includes(wfStageCanon);

  const canComplete = wfStageCanon === "verification_passed";

  const backToNotifications = () => navigate("/market/notifications");

  const statusPillClass = (status) => {
    const s = normLower(status);
    if (s === "accepted") return "tod-pill tod-pill--success";
    if (s === "declined") return "tod-pill tod-pill--danger";
    if (s === "pending") return "tod-pill tod-pill--warn";
    return "tod-pill";
  };

  return (
    <div className="trade-offer-detail">
      <div className="tod-container">
        <div className="tod-header">
          <div className="tod-title-wrap">
            <h2 className="tod-title">Offers</h2>
            <p className="tod-subtitle">
              {trade?.title ? (
                <>
                  <strong>{trade.title}</strong>{" "}
                  <span className="tod-muted-inline">•</span>{" "}
                  <span className="tod-muted-inline tod-mono">{tradeId}</span>
                </>
              ) : (
                <span className="tod-mono">{tradeId}</span>
              )}
            </p>
          </div>

          <div className="tod-header-actions">
            <button type="button" className="tod-btn" onClick={backToNotifications}>
              ← Back to Notifications
            </button>

            <button type="button" className="tod-btn" onClick={() => setWfOpen(true)}>
              Open Workflow
            </button>

            <button
              type="button"
              className="tod-btn tod-btn--primary"
              onClick={() => navigate(`/trade/${tradeId}`)}
            >
              View Trade
            </button>
          </div>
        </div>

        {actionBanner.text ? (
          <div
            className={`tod-card`}
            style={{
              marginTop: 14,
              borderColor:
                actionBanner.type === "success"
                  ? "rgba(16, 185, 129, 0.28)"
                  : "rgba(239, 68, 68, 0.24)",
              background:
                actionBanner.type === "success"
                  ? "rgba(16, 185, 129, 0.08)"
                  : "rgba(239, 68, 68, 0.08)",
            }}
          >
            <div className="tod-card-body">
              <div
                style={{
                  fontWeight: 800,
                  color:
                    actionBanner.type === "success" ? "#065f46" : "#991b1b",
                }}
              >
                {actionBanner.text}
              </div>
            </div>
          </div>
        ) : null}

        {sessionLoading ? (
          <div className="tod-card" style={{ marginTop: 14 }}>
            <div className="tod-card-header">
              <h3 className="tod-card-title">Session</h3>
              <span className="tod-pill">Loading</span>
            </div>
            <div className="tod-card-body">
              <div className="tod-empty">Loading session…</div>
            </div>
          </div>
        ) : !canView ? (
          <div className="tod-card" style={{ marginTop: 14 }}>
            <div className="tod-card-header">
              <h3 className="tod-card-title">Verifying access</h3>
              <span className="tod-pill">
                {isAdmin ? "Admin support" : "Seller console"}
              </span>
            </div>
            <div className="tod-card-body">
              <div className="tod-empty">
                Checking trade ownership…
                <div style={{ marginTop: 8 }}>
                  Trade: <code className="tod-mono">{tradeId}</code>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="tod-actions" style={{ marginTop: 0 }}>
              <span className="tod-pill tod-pill--primary">
                {totalOffers} {totalOffers === 1 ? "offer" : "offers"}
              </span>

              {highestCash != null && (
                <span className="tod-pill">
                  Highest cash:{" "}
                  {highestCash.toLocaleString("en-ZA", {
                    style: "currency",
                    currency: "ZAR",
                  })}
                </span>
              )}

              {latestTs && (
                <span className="tod-pill">Latest: {formatRelativeTime(latestTs)}</span>
              )}

              {VIEW_MODE === "admin" && tradeStatus && (
                <span className="tod-pill">
                  Trade status:{" "}
                  <strong style={{ textTransform: "capitalize" }}>{tradeStatus}</strong>
                </span>
              )}

              {VIEW_MODE === "admin" && (
                <span className="tod-pill">
                  Workflow:{" "}
                  <strong style={{ textTransform: "capitalize" }}>
                    {prettyStage(wfStageCanon || wfStageRaw)}
                  </strong>
                </span>
              )}

              {VIEW_MODE === "admin" && isCashTrade && (
                <span className="tod-pill">
                  Payment: <strong>{paymentConfirmed ? "Confirmed" : "Pending"}</strong>
                </span>
              )}

              {VIEW_MODE === "admin" && (
                <span className="tod-pill">
                  Route: <strong>{routeLabel}</strong>
                </span>
              )}

              {VIEW_MODE === "admin" && (
                <span className="tod-pill tod-admin-pill">Admin view enabled</span>
              )}
            </div>

            <div className="tod-grid" style={{ marginTop: 14 }}>
              <div>
                <OfferInboxPanel
                  offers={offers}
                  loadingOffers={loadingOffers}
                  selectedOfferId={selectedOfferId}
                  tradeId={tradeId}
                  navigate={navigate}
                  setSelectedOfferId={setSelectedOfferId}
                  didAutoSelectRef={didAutoSelectRef}
                  isAdmin={isAdmin}
                  tradeOwnerUid={tradeOwnerUid}
                  statusPillClass={statusPillClass}
                />
              </div>

              <div>
                <OfferDecisionPanel
                  selectedOffer={selectedOffer}
                  copied={copied}
                  copyToClipboard={copyToClipboard}
                  decide={decide}
                  statusPillClass={statusPillClass}
                  actionBusy={actionBusy}
                />

                {VIEW_MODE === "admin" && (
                  <>
                    <AdminWorkflowPanel
                      selectedOffer={selectedOffer}
                      loadingPrivate={loadingPrivate}
                      privateOffer={privateOffer}
                      loadingWorkflow={loadingWorkflow}
                      wfStageCanon={wfStageCanon}
                      wfStageRaw={wfStageRaw}
                      workflow={workflow}
                      trade={trade}
                      paymentConfirmed={paymentConfirmed}
                      loadingLogistics={loadingLogistics}
                      logistics={logistics}
                      wfTradeType={wfTradeType}
                      isCashTrade={isCashTrade}
                      grossAmount={grossAmount}
                      platformFee={platformFee}
                      netToSeller={netToSeller}
                      adminNote={adminNote}
                      setAdminNote={setAdminNote}
                      pickupStart={pickupStart}
                      setPickupStart={setPickupStart}
                      pickupEnd={pickupEnd}
                      setPickupEnd={setPickupEnd}
                      deliveryStart={deliveryStart}
                      setDeliveryStart={setDeliveryStart}
                      deliveryEnd={deliveryEnd}
                      setDeliveryEnd={setDeliveryEnd}
                      canInspect={canInspect}
                      canStartPickup={canStartPickup}
                      canSchedulePickup={canSchedulePickup}
                      canScheduleDelivery={canScheduleDelivery}
                      canComplete={canComplete}
                      onSchedulePickup={onSchedulePickup}
                      onRecordInspection={onRecordInspection}
                      onStartPickup={onStartPickup}
                      onScheduleDelivery={onScheduleDelivery}
                      onCompleteTrade={onCompleteTrade}
                      routeLabel={routeLabel}
                    />

                    <ProcessTimelinePanel
                      loadingTimeline={loadingTimeline}
                      timeline={timeline}
                    />
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <TradeWorkflowModal
        open={wfOpen}
        onClose={() => setWfOpen(false)}
        tradeId={tradeId}
        mode={WORKFLOW_MODAL_MODE}
      />
    </div>
  );
};

export default TradeOfferDetail;